import {useState, useMemo, useRef, Dispatch, SetStateAction, MouseEventHandler} from 'react'
import CronParser from 'cron-parser'
import {get, isObject, isNil, omit} from 'lodash'

import {selectTheme, selectMethod, selectWorkspaces, createDispatchActions} from '../../store'
import {RIF, useClickOutside, _, t, useCurrentProjectState, useCurrentWorkspaceState, darken, setAlpha} from '../../lib'
import {convertMovesenseDescription, convertGarminStreamDescription, DragHandle, TaskScheduleCard, ButtonMore} from '..'
import {
  TaskStateType,
  getTaskContentName,
  TempTask,
  TempTaskScheduleActionType,
  TempTaskSchedule,
  sanitizeDuplicatedSection,
  fetchDisplayStrategyListFromQuestionnaire,
  ActionableTempTaskSchedule,
} from '../../model'
import {differenceInMilliseconds} from 'date-fns'
import {
  ReminderItem,
  TaskType,
  IStopwatchMovesenseStream,
  IStopwatchGarminStream,
  SelectInputConfig,
  NumberInputConfig,
  ITaskSchedule,
  ScheduleType,
  IQuestionnaireSection,
} from '../../shared/db'
import {Draggable} from 'react-beautiful-dnd'
import {
  BellIcon,
  DeviceIcon,
  MovesenseImg,
  RepeatIcon,
  SurveyBlackIcon,
  TimerBlackIcon,
  TodoBlackIcon,
} from '../../asset/image'

export interface TaskTagPropsT {
  isLiveEditing: boolean
  isTaskPendingToBeCreated: boolean
  task: TaskStateType | TempTask
  setRemovingTaskId?: Dispatch<SetStateAction<string>>
  setRemovingTaskName?: Dispatch<SetStateAction<string>>
  archiveTask: (task: TempTask) => void
  index: number
  onEditing?: () => void
  key?: string
  isViewOnly?: boolean
}

function isSelectInputConfig(inputConfig: unknown): inputConfig is SelectInputConfig {
  if (isObject(inputConfig)) {
    if (get(inputConfig, 'options')) {
      return true
    }
  }
  return false
}

function isNumberInputConfig(inputConfig: unknown): inputConfig is NumberInputConfig {
  if (isObject(inputConfig)) {
    for (const field of ['integer', 'max', 'min']) {
      if (get(inputConfig, field)) {
        return true
      }
    }
  }
  return false
}

export const TaskTag = (props: TaskTagPropsT) => {
  const {color, pad, fontWeight, fontSize} = selectTheme()

  const {
    isLiveEditing,
    isTaskPendingToBeCreated,
    task,
    setRemovingTaskId,
    setRemovingTaskName,
    archiveTask,
    index,
    onEditing,
    isViewOnly,
  } = props
  const taskContentName = getTaskContentName(task.type)

  const {doREQUEST_TASK_DUPLICATE, doTEMP_TASK_DATA_ADD_TASK} = createDispatchActions()
  const methodState = selectMethod()
  const methodId = methodState.id
  const {projectId, project} = useCurrentProjectState()
  const isEditingLiveProject = project?.status === 'live'

  const tempScheudleToStubSchedule = (taskId: string, tempSchedule: TempTaskSchedule): ITaskSchedule | null => {
    const {type, ...scheduleValues} = tempSchedule.values
    if (!type) {
      return null
    } else {
      return {
        id: tempSchedule.tempId,
        createdAt: new Date(), // stub value
        updatedAt: new Date(), // stub value
        taskId,
        type: type as ScheduleType,
        inactive: true,
        current: false,
        ...scheduleValues,
      }
    }
  }

  const definiteScheduleList = useMemo(() => {
    const currentScheduleList = [...(task.scheduleList ?? [])]
      .filter((schedule) => schedule.current)
      .sort((a, b) => {
        return differenceInMilliseconds(new Date(b.createdAt), new Date(a.createdAt))
      })

    if (isLiveEditing) {
      const taskInLiveEditing = task as TempTask
      if (taskInLiveEditing.liveEditingScheduleQueue) {
        taskInLiveEditing.liveEditingScheduleQueue.forEach((tempSchedule) => {
          switch (tempSchedule.action) {
            case TempTaskScheduleActionType.Create: {
              const schedule = tempScheudleToStubSchedule(taskInLiveEditing.id, tempSchedule)
              if (!schedule) {
                return null
              } else {
                currentScheduleList.push(schedule)
              }
              break
            }
            case TempTaskScheduleActionType.Update: {
              const updateItemIndex = currentScheduleList.findIndex((s) => s.id === tempSchedule.values.id)
              if (updateItemIndex > -1) {
                const schedule = tempScheudleToStubSchedule(taskInLiveEditing.id, tempSchedule)
                if (!schedule) {
                  return null
                } else {
                  currentScheduleList[updateItemIndex] = schedule
                }
              }
              break
            }
            case TempTaskScheduleActionType.Delete: {
              const deleteItemIndex = currentScheduleList.findIndex((s) => s.id === tempSchedule.values.id)
              if (deleteItemIndex > -1) {
                currentScheduleList.splice(deleteItemIndex, 1)
              }
              break
            }
          }
        })
      }
    }
    return currentScheduleList
  }, [task, task.scheduleList])

  const workspacesState = selectWorkspaces()
  const {workspaceId} = useCurrentWorkspaceState()
  const role = workspacesState.roleInWorkspaces[workspaceId ?? ''] || 'owner'
  const canEdit = role !== 'viewer' && !isViewOnly
  const isEventTrackerTask = !isNil(task.eventTracker)

  const [hovered, setHovered] = useState(false)
  const [showingDropdown, setShowingDropdown] = useState(false)
  const isDraggingRef = useRef(false)

  const brickRef = useRef<HTMLInputElement | null>(null)
  useClickOutside(brickRef, () => setShowingDropdown(false))

  const handleClickMoreBtn = () => {
    setShowingDropdown(true)
  }

  const handleMouseEnter = () => {
    setHovered(true)
  }

  const handleMouseLeave = () => {
    setHovered(false)
  }

  const handleClick: MouseEventHandler = (e) => {
    // react-beautiful-dnd may still trigger a click on mouse up after dragging.
    // If we open editors during drag cleanup, it can cause react-beautiful-dnd invariants.
    if (isDraggingRef.current) return
    const target = e.target as EventTarget & {tagName?: string}
    if (target.tagName !== 'BUTTON') {
      onEditing?.()
    }
  }

  const handleDeleteBtnClick = () => {
    if (isEditingLiveProject) {
      archiveTask(task as TempTask)
    } else {
      if (setRemovingTaskId) setRemovingTaskId(task.id)
      if (setRemovingTaskName) setRemovingTaskName(task[taskContentName]?.name ?? '')
    }
  }

  const generateTaskTagContent = () => {
    let deviceTaskContent = ''
    if (task.type === TaskType.StopwatchMovesenseStream || task.type === TaskType.StopwatchMovesenseLogData) {
      deviceTaskContent = convertMovesenseDescription(task[taskContentName] as IStopwatchMovesenseStream)
    }
    if (task.type === TaskType.StopwatchGarminStream) {
      deviceTaskContent = convertGarminStreamDescription(task[taskContentName] as IStopwatchGarminStream)
    }

    return deviceTaskContent
  }

  const formatCronString = (cronParseObj: CronParser.CronExpression) => {
    const {dayOfWeek, hour, minute} = cronParseObj.fields

    const timeString = `${hour[0].toString().padStart(2, '0')}:${minute[0].toString().padStart(2, '0')}`
    const AMPM = hour[0] < 12 ? 'AM' : 'PM'

    // (0 or 7 is Sun) from 'cron-parser'
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    const formattedDaysOfWeek = dayOfWeek.map((index: number) => daysOfWeek[index])

    let formattedString = 'Repeats'

    if (dayOfWeek.length >= 7) {
      formattedString += ` daily at ${timeString} ${AMPM}`
    } else if (dayOfWeek.length === 1) {
      formattedString += ` every ${formattedDaysOfWeek[0]} at ${timeString} ${AMPM}`
    } else {
      const lastDayOfWeek = formattedDaysOfWeek.pop()
      formattedString += ` every ${formattedDaysOfWeek.join(', ')} and ${lastDayOfWeek} at ${timeString} ${AMPM}`
    }
    return formattedString
  }

  const formatISOString = (dateString: string) => {
    const utcDate = new Date(dateString)
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }
    const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000
    return new Date(utcDate.getTime() + timezoneOffset).toLocaleString(undefined, options)
  }

  const generateReminderString = (reminder: ReminderItem) => {
    if (reminder.type === 'cron') {
      const cronInterval = CronParser.parseExpression(reminder.value)
      return formatCronString(cronInterval)
    } else if (reminder.type === 'date') {
      return formatISOString(reminder.value)
    }
  }

  const duplicateTask = () => {
    const originTask = _.cloneDeep(task)
    const originTaskContentName = getTaskContentName(originTask.type)
    if (!isEditingLiveProject) {
      const newTask = {
        taskId: originTask.id,
        methodId,
        name: `${originTask[originTaskContentName]?.name}_copy`,
      }
      doREQUEST_TASK_DUPLICATE({
        payload: newTask,
      })
    } else {
      const replicaTempTaskId = t.uuid()
      const liveEditingScheduleQueueForReplica: ActionableTempTaskSchedule[] = []
      if (definiteScheduleList.length > 0) {
        definiteScheduleList.forEach((schedule) => {
          const {id, createdAt, updatedAt, current, inactive, taskId, ...scheduleValues} = schedule
          liveEditingScheduleQueueForReplica.push({
            // for duplicated task, all schedules in displayedScheduleList should be treated as new schedules
            // to be created, no matter they are from original scheduleList or from liveEditingScheduleQueue
            action: TempTaskScheduleActionType.Create,
            tempId: t.uuid(),
            createdAt: new Date(),
            values: {
              ...scheduleValues,
              taskId: replicaTempTaskId,
            },
          })
        })
      }

      const replicaTempTask: TempTask = {
        id: replicaTempTaskId,
        required: false,
        workspaceId: workspaceId ?? '',
        projectId: projectId ?? '',
        methodId: methodId ?? '',
        enabled: true,
        color: task.color,
        actionType: 'create',
        scheduleRepeat: originTask.scheduleRepeat,
        scheduleDescription: originTask.scheduleDescription?.length ? originTask.scheduleDescription : undefined,
        liveEditingScheduleQueue: liveEditingScheduleQueueForReplica,
        eventTracker: originTask.eventTracker,
        reminderList: [],
        type: originTask.type,
        [originTaskContentName]: {
          name: `${originTask[originTaskContentName]?.name}_copy`,
          description: originTask[originTaskContentName]?.description,
          projectId,
          taskId: replicaTempTaskId,
        },
      }
      if (originTask.type === TaskType.Questionnaire) {
        if (originTask.questionnaire && replicaTempTask.questionnaire) {
          if (isTaskPendingToBeCreated) {
            // dulicate from temp task, directly clone sectionList and displayStrategyList
            replicaTempTask.questionnaire.sectionList = _.cloneDeep(originTask.questionnaire.sectionList)
            const originDisplayStrategyList = (originTask as TempTask).displayStrategyList
            if (originDisplayStrategyList) {
              replicaTempTask.displayStrategyList = _.cloneDeep(originDisplayStrategyList)
            }
          } else {
            // duplicate from existing task, need to sanitize duplicated sections
            replicaTempTask.questionnaire.sectionList = (
              _.cloneDeep(originTask.questionnaire.sectionList) as IQuestionnaireSection[]
            ).map(sanitizeDuplicatedSection)

            replicaTempTask.displayStrategyList = fetchDisplayStrategyListFromQuestionnaire(
              replicaTempTask.questionnaire,
            )
          }
        }
      }
      doTEMP_TASK_DATA_ADD_TASK(replicaTempTask)
    }
    setShowingDropdown(false)
  }

  const taskColor = task?.color ? `#${task.color}` : color.primary

  const taskTagStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    borderRadius: '5px',
    border: `1px solid ${hovered ? color.grey_400 : color.grey_160}`,
    borderLeft: `6px solid ${taskColor}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `${pad.medium} ${pad.mediumSmall}`,
    gap: '8px',
    background: hovered ? color.grey_20 : color.white,
    cursor: 'pointer',
  }

  const eventTrackerTaskTagStyle: React.CSSProperties = {
    ...omit(taskTagStyle, 'borderLeft', 'border', 'background'),
    border: `1px solid ${hovered ? darken(taskColor, 10) : taskColor}`,
    background: setAlpha(taskColor, hovered ? 0.18 : 0.08),
  }

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => {
        isDraggingRef.current = snapshot.isDragging
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            css={{
              width: '100%',
              // height: addingToTaskSet? '3.5rem': '5rem',
              height: 'fit-content',
              position: 'relative',
              marginBottom: pad.mediumSmall,
              display: 'flex',
            }}>
            <DragHandle
              {...provided.dragHandleProps}
              isDragging={snapshot.isDragging}
              isShowing={canEdit && (hovered || snapshot.isDragging)}
              css={{
                height: '100%',
                width: '24px',
                position: 'absolute',
                left: '-24px',
              }}
            />
            <div
              data-testid='task_tag'
              onClick={handleClick}
              css={{
                ...(isEventTrackerTask ? eventTrackerTaskTagStyle : taskTagStyle),
              }}>
              <div css={{display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center'}}>
                <div css={{display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start'}}>
                  {RIF(task.type === TaskType.Todo, <img src={TodoBlackIcon} width='24' height='24' />)}
                  {RIF(task.type === TaskType.Timer, <img src={TimerBlackIcon} width='24' height='24' />)}
                  {RIF(task.type === TaskType.Questionnaire, <img src={SurveyBlackIcon} width='24' height='24' />)}
                  {RIF(
                    task.type === TaskType.StopwatchMovesenseStream || task.type === TaskType.StopwatchMovesenseLogData,
                    <img src={MovesenseImg} width='24' height='24' css={{transform: 'scale(1.2)'}} />,
                  )}
                  {RIF(
                    task.type === TaskType.StopwatchGarminStream,
                    <img src={DeviceIcon} width='22' height='22' css={{transform: 'scale(1.2)'}} />,
                  )}
                  {RIF(task[taskContentName]?.name, (name) => (
                    <span css={{fontWeight: fontWeight.thick, fontSize: fontSize.h6}}>{name}</span>
                  ))}
                </div>
                {RIF(canEdit, <ButtonMore data-testid='task_tag_more_btn' onClick={handleClickMoreBtn} />)}
              </div>
              {RIF(
                task.scheduleRepeat || task.scheduleDescription,
                <div css={{display: 'flex', alignItems: 'center', gap: '3px'}}>
                  {RIF(task.scheduleRepeat, <img src={RepeatIcon} width='14' height='14' />)}
                  {RIF(task.scheduleDescription, (description) => (
                    <p css={{color: color.grey_400, fontSize: fontSize.h7}}>{description}</p>
                  ))}
                </div>,
              )}
              {RIF(generateTaskTagContent(), (content) => (
                <div
                  css={{
                    background: color.background,
                    borderRadius: '8px',
                    padding: '8px 12px',
                    color: color.grey_600,
                    fontSize: '12px',
                  }}>
                  {content}
                </div>
              ))}
              {RIF(
                definiteScheduleList.length,
                <div css={{width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  {definiteScheduleList.map((schedule) => (
                    <TaskScheduleCard
                      key={schedule.id}
                      schedule={schedule}
                      isViewOnly={true}
                      style={{
                        border: `1px solid ${color.border.default}`,
                        borderRadius: '5px',
                      }}
                    />
                  ))}
                </div>,
              )}
              {task.reminderList?.map((reminder, index) => (
                <div
                  key={index}
                  css={{
                    background: color.background,
                    borderRadius: '8px',
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                  }}>
                  <img src={BellIcon} width='18' css={{marginRight: '5px'}} />
                  {RIF(generateReminderString(reminder), (reminderString) => (
                    <p css={{color: color.grey_600, fontSize: '12px'}}>{reminderString}</p>
                  ))}
                </div>
              ))}
            </div>
            <div
              ref={brickRef}
              css={{
                position: 'relative',
                width: '1.71rem',
                height: '1.71rem',
              }}>
              {RIF(
                canEdit && showingDropdown,
                <div
                  css={{
                    width: '100px',
                    borderRadius: '5px',
                    boxShadow: '0px 4px 10px 1px rgba(104, 104, 104, 0.25)',
                    border: `1px solid ${color.border.disabled}`,
                    backgroundColor: color.white,
                    zIndex: '99',
                    position: 'absolute',
                    top: '1.68rem',
                    right: '1rem',
                    padding: '8px 0',
                  }}>
                  <button
                    data-testid='task_tag_duplicate_btn'
                    onClick={duplicateTask}
                    css={{
                      width: '100%',
                      height: '28px',
                      padding: '5px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: fontWeight.regular,
                      textAlign: 'start',
                      ':hover': {
                        backgroundColor: color.hover,
                      },
                      ':focus': {outline: 'none'},
                    }}>
                    Duplicate
                  </button>
                  <button
                    data-testid='task_tag_remove_btn'
                    onClick={handleDeleteBtnClick}
                    css={{
                      width: '100%',
                      height: '28px',
                      padding: '5px 16px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: fontWeight.regular,
                      textAlign: 'start',
                      ':hover': {
                        backgroundColor: color.hover,
                      },
                      ':focus': {outline: 'none'},
                    }}>
                    {isEditingLiveProject ? 'Archive' : 'Delete'}
                  </button>
                </div>,
              )}
            </div>
          </div>
        )
      }}
    </Draggable>
  )
}
